import * as Sentry from '@sentry/react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router'

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

import { GlobalErrorBoundary } from './components/GlobalErrorBoundary'
import { usePartner } from './context/PartnerContext'
import Icons from './icons/icons'
import { AuthLayout } from './layouts/AuthLayout'
/* Import layouts */
import { LeasingWizardLayout } from './layouts/LeasingWizardLayout'
import { WizardLayout } from './layouts/WizardLayout'
import { IndexPage } from './pages'
import { UnauthorizedPage } from './pages/403'
import { NotFoundPage } from './pages/404'
import { LoginPage } from './pages/auth/login'
import { ResetPasswordPage } from './pages/auth/resetPassword'
import { ErrorPage } from './pages/ErrorPage'
import { FlexGroupCreatePage } from './pages/flex-group/create'
import { FlexGroupDetailPage } from './pages/flex-group/detail'
import { FlexGroupEditPage } from './pages/flex-group/edit'
import { FlexGroupListPage } from './pages/flex-group/list'
import { InitMoveToBroker } from './pages/InitMoveToBroker'
/* Import pages */
import { PoleasErrorBoundary } from './containers/poleas/PoleasErrorBoundary'
import { Apps } from './dtos'
import { AppLayout } from './layouts/AppLayout'
import { BasicLayout } from './layouts/BasicLayout'
import { LeasingBaseLayout } from './layouts/LeasingBaseLayout'
import { PoleasAuthLayout } from './layouts/PoleasAuthLayout'
import { PoleasBaseLayout } from './layouts/PoleasBaseLayout'
import { UnauthorizedPageBmw } from './pages/403bmw'
import { LogDetailPage } from './pages/api/LogDetailPage'
import { AppOffline } from './pages/AppOffline'
import LeasingLogin from './pages/LeasingLogin'
import { LeasingPage } from './pages/leasingSimpleWizard/leasingPageWrapper'
import { NewsAdminPage } from './pages/news/admin'
import { NewsCreatePage } from './pages/news/create'
import { AppInfoEditPage } from './pages/news/edit'
import { NewsPage } from './pages/news/news'
import { OrganizationCreatePage } from './pages/organization/create'
import { OrganizationCreateDependentPage } from './pages/organization/createDependent'
import { OrganizationCreateUserPage } from './pages/organization/createUser'
import { OrganizationCurrentPage } from './pages/organization/current'
import { OrganizationDetailPage } from './pages/organization/detail'
import { OrganizationEditPage } from './pages/organization/edit'
import { OrganizationListPage } from './pages/organization/list'
import { PoleasImportPage } from './pages/poleas/poleasImportPage'
import { PoleasImportsPage } from './pages/poleas/PoleasImportsPage'
import { PoleasNewPage } from './pages/poleas/poleasNewPage'
import { Step1Page as PoleasStep1 } from './pages/poleas/wizard/1'
import { Step2Page as PoleasStep2 } from './pages/poleas/wizard/2'
import { Step3Page as PoleasStep3 } from './pages/poleas/wizard/3'
import { Step4Page as PoleasStep4 } from './pages/poleas/wizard/4'
import { StepCompletePage as PoleasComplete } from './pages/poleas/wizard/complete'
import { PoleasLogout } from './pages/poleas/wizard/logout'
import { SignInPage as PoleasSignInPage } from './pages/poleas/wizard/SignInPage'
import { PpzAuthorizationCreatePage } from './pages/ppz-authorization/create'
import { PpzAuthorizationDetailPage } from './pages/ppz-authorization/detail'
import { PpzAuthorizationListPage } from './pages/ppz-authorization/list'
import { ProfilePage } from './pages/profile'
import { ConceptsPropertyPage } from './pages/property/concepts'
import { ContractsPropertyPage } from './pages/property/contracts'
import { EmailLogoutPage } from './pages/public/email/EmailLogoutPage'
import { EmailPage } from './pages/public/email/EmailPage'
import { EmailSignInPage } from './pages/public/email/EmailSignInPage'
import { Documents } from './pages/static/Documents'
import { Insurer } from './pages/static/insurer/Insurer'
import { WorkGuidelines } from './pages/static/WorkGuidelines'
import { Step1Page as PropertyStep1 } from './pages/steps/property/1'
import { Step2Page as PropertyStep2 } from './pages/steps/property/2'
import { Step3Page as PropertyStep3 } from './pages/steps/property/3'
import { Step4Page as PropertyStep4 } from './pages/steps/property/4'
import { StepCompletePage as PropertyStepComplete } from './pages/steps/property/complete'
import { Step1Page as TravelStep1 } from './pages/steps/travel/1'
import { Step2Page as TravelStep2 } from './pages/steps/travel/2'
import { Step3Page as TravelStep3 } from './pages/steps/travel/3'
import { Step4Page as TravelStep4 } from './pages/steps/travel/4'
import { StepCompletePage as TravelStepComplete } from './pages/steps/travel/complete'
import { Step1Page } from './pages/steps/vehicle/1'
import { Step2Page } from './pages/steps/vehicle/2'
import { Step3Page } from './pages/steps/vehicle/3'
import { Step4Page } from './pages/steps/vehicle/4'
import { StepCompletePage } from './pages/steps/vehicle/complete'
import { Step1Page as ShortInsurancePage } from './pages/steps/vehicle/dailyPov/1'
import { Step1Page as ExtraStep1 } from './pages/steps/vehicle/extra/1'
import { Step2Page as ExtraStep2 } from './pages/steps/vehicle/extra/2'
import { Step3Page as ExtraStep3 } from './pages/steps/vehicle/extra/3'
import { Step4Page as ExtraStep4 } from './pages/steps/vehicle/extra/4'
import { StepCompletePage as ExtraStepComplete } from './pages/steps/vehicle/extra/complete'
import { DiscountsOverviewPage } from './pages/supervizor/DiscountsOverview'
import { ExportsPage } from './pages/supervizor/ExportsPage'
import { SetCommissionPaidDates } from './pages/supervizor/SetCommissionPaidDates'
import { DummyPersonsPage } from './pages/supervizor/DummyPersonsPage'
import { DummyPersonEditPage } from './pages/supervizor/DummyPersonEditPage'
import { StaticFilesPage } from './pages/supervizor/StaticFilesPage'
import { ConceptsTravelPage } from './pages/travel/concepts'
import { UserDetailPage } from './pages/user/detail'
import { UserEditPage } from './pages/user/edit'
import { UserListPage } from './pages/user/list'
import { ConceptsCarPage } from './pages/vehicle/concepts'
import { ContractsCarPage } from './pages/vehicle/contracts'
import { ShortInsurancesCarPage } from './pages/vehicle/shortInsurances'

export const Router = () => {
  const { getBaseUrl } = usePartner()

  const routes = createRoutesFromElements(
    <Route ErrorBoundary={GlobalErrorBoundary}>
      <Route path="/manage/icons" element={<Icons />}></Route>

      <Route path="/auth">
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/login" element={<LoginPage />} />
      </Route>

      <Route path="/public/email/login" element={<EmailSignInPage />} />
      <Route path="/public/email/logout" element={<EmailLogoutPage />} />
      <Route path="/public/email" element={<EmailPage />} />

      <Route element={<LeasingBaseLayout app={Apps.leasingSimplifiedWizard} />}>
        <Route path="/initMove" element={<InitMoveToBroker />} />
        <Route path="/initMoveBmw" element={<InitMoveToBroker showBmwCallbackUrl />} />
        <Route path="/unauthorized/:insuranceParamsId" element={<UnauthorizedPageBmw />} />
        <Route path="/login/:token" element={<LeasingLogin />} />
      </Route>

      <Route element={<PoleasBaseLayout />} ErrorBoundary={PoleasErrorBoundary}>
        <Route path="/poleas/signin/:token" element={<PoleasSignInPage />} />
        <Route path="/poleas/logout" element={<PoleasLogout />} />
        <Route element={<PoleasAuthLayout />}>
          <Route path="/poleas/wizard/1" element={<PoleasStep1 />} />
          <Route path="/poleas/wizard/2" element={<PoleasStep2 />} />
          <Route path="/poleas/wizard/3" element={<PoleasStep3 />} />
          <Route path="/poleas/wizard/4" element={<PoleasStep4 />} />
          <Route path="/poleas/complete" element={<PoleasComplete />} />
        </Route>
      </Route>

      <Route path="/wizard/:insuranceParamsId" element={<LeasingWizardLayout app={Apps.leasingSimplifiedWizard} />}>
        <Route index element={<LeasingPage />} />
      </Route>

      <Route path="/VWFS/wizard/:insuranceParamsId" element={<LeasingWizardLayout app={Apps.leasingSimplifiedWizard} />}>
        <Route index element={<LeasingPage />} />
      </Route>

      <Route element={<AuthLayout />}>
        <Route path='/' element={<AppLayout app={Apps.vehicle} />}>
          <Route element={<BasicLayout />}>
            <Route index element={<IndexPage />} />
            <Route path="/concepts" element={<ConceptsCarPage />} />
            <Route path="/contracts" element={<ContractsCarPage />} />
            <Route path="/vehicle/concepts" element={<ConceptsCarPage />} />
            <Route path="/vehicle/contracts" element={<ContractsCarPage />} />
            <Route path="/vehicle/news" element={<NewsPage />} />
            <Route path="/vehicle/documents" element={<Documents />} />
            <Route path="/vehicle/workGuidelines" element={<WorkGuidelines />} />
            <Route path="/vehicle/insurer/:insurerId" element={<Insurer />} />

            <Route path="/poleas/import" element={<PoleasImportPage />} />
            <Route path="/poleas/imports" element={<PoleasImportsPage />} />
            <Route path="/poleas/new" element={<PoleasNewPage />} />
          </Route>

          <Route path="/steps" element={<WizardLayout />}>
            <Route path="/steps/1" element={<Step1Page />} />
            <Route path="/steps/1/:insuranceParamsId" element={<Step1Page />} />
            <Route path="/steps/2/:insuranceParamsId" element={<Step2Page />} />
            <Route path="/steps/3/:insuranceParamsId" element={<Step3Page />} />
            <Route path="/steps/4/:insuranceParamsId" element={<Step4Page />} />
            <Route path="/steps/complete/:insuranceParamsId" element={<StepCompletePage />} />
          </Route>
        </Route>

        <Route path='/leasing' element={<AppLayout app={Apps.leasing} />}>
          <Route element={<BasicLayout />}>
            <Route index element={<IndexPage />} />
            <Route path="/leasing/news" element={<NewsPage />} />
            <Route path="/leasing/concepts" element={<ConceptsCarPage />} />
            <Route path="/leasing/contracts" element={<ContractsCarPage />} />
          </Route>

          <Route path="/leasing/steps" element={<WizardLayout />}>
            <Route path="/leasing/steps/1" element={<Step1Page />} />
            <Route path="/leasing/steps/1/:insuranceParamsId" element={<Step1Page />} />
            <Route path="/leasing/steps/2/:insuranceParamsId" element={<Step2Page />} />
            <Route path="/leasing/steps/3/:insuranceParamsId" element={<Step3Page />} />
            <Route path="/leasing/steps/4/:insuranceParamsId" element={<Step4Page />} />
            <Route path="/leasing/steps/complete/:insuranceParamsId" element={<StepCompletePage />} />
          </Route>
        </Route>

        <Route path='/fleet' element={<AppLayout app={Apps.fleet} />}>
          <Route element={<BasicLayout />}>
            <Route index element={<IndexPage />} />
            <Route path="/fleet/news" element={<NewsPage />} />
            <Route path="/fleet/concepts" element={<ConceptsCarPage />} />
            <Route path="/fleet/contracts" element={<ContractsCarPage />} />
          </Route>

          <Route path="/fleet/steps" element={<WizardLayout />}>
            <Route path="/fleet/steps/1" element={<Step1Page />} />
            <Route path="/fleet/steps/1/:insuranceParamsId" element={<Step1Page />} />
            <Route path="/fleet/steps/2/:insuranceParamsId" element={<Step2Page />} />
            <Route path="/fleet/steps/3/:insuranceParamsId" element={<Step3Page />} />
            <Route path="/fleet/steps/4/:insuranceParamsId" element={<Step4Page />} />
            <Route path="/fleet/steps/complete/:insuranceParamsId" element={<StepCompletePage />} />
          </Route>
        </Route>

        <Route path='/travel' element={<AppLayout app={Apps.travel} />}>
          <Route element={<BasicLayout />}>
            <Route index element={<IndexPage />} />
            <Route path="/travel/news" element={<NewsPage />} />
            <Route path="/travel/concepts" element={<ConceptsTravelPage />} />
            <Route path="/travel/contracts" element={<ContractsCarPage />} />
          </Route>

          <Route path="/travel/steps" element={<WizardLayout />}>
            <Route path="/travel/steps/1" element={<TravelStep1 />} />
            <Route path="/travel/steps/1/:insuranceParamsId" element={<TravelStep1 />} />
            <Route path="/travel/steps/2/:insuranceParamsId" element={<TravelStep2 />} />
            <Route path="/travel/steps/3/:insuranceParamsId" element={<TravelStep3 />} />
            <Route path="/travel/steps/4/:insuranceParamsId" element={<TravelStep4 />} />
            <Route path="/travel/steps/complete/:insuranceParamsId" element={<TravelStepComplete />} />
          </Route>
        </Route>

        <Route path='/property' element={<AppLayout app={Apps.property} />}>
          <Route element={<BasicLayout />}>
            <Route index element={<IndexPage />} />
            <Route path="/property/news" element={<NewsPage />} />
            <Route path="/property/concepts" element={<ConceptsPropertyPage />} />
            <Route path="/property/contracts" element={<ContractsPropertyPage />} />
          </Route>

          <Route path="/property/steps" element={<WizardLayout />}>
            <Route path="/property/steps/1" element={<PropertyStep1 />} />
            <Route path="/property/steps/1/:insuranceParamsId" element={<PropertyStep1 />} />
            <Route path="/property/steps/2/:insuranceParamsId" element={<PropertyStep2 />} />
            <Route path="/property/steps/3/:insuranceParamsId" element={<PropertyStep3 />} />
            <Route path="/property/steps/4/:insuranceParamsId" element={<PropertyStep4 />} />
            <Route path="/property/steps/complete/:insuranceParamsId" element={<PropertyStepComplete />} />
          </Route>
        </Route>

        <Route path="/extra" element={<AppLayout app={Apps.extra} />}>
          <Route element={<WizardLayout />}>
            <Route path="/extra/steps/1" element={<ExtraStep1 />} />
            <Route path="/extra/steps/1/:insuranceParamsId" element={<ExtraStep1 />} />
            <Route path="/extra/steps/2/:insuranceParamsId" element={<ExtraStep2 />} />
            <Route path="/extra/steps/3/:insuranceParamsId" element={<ExtraStep3 />} />
            <Route path="/extra/steps/4/:insuranceParamsId" element={<ExtraStep4 />} />
            <Route path="/extra/steps/complete/:insuranceParamsId" element={<ExtraStepComplete />} />
          </Route>
        </Route>

        <Route element={<AppLayout app={Apps.shortInsurance} />}>
          <Route element={<BasicLayout />}>
            <Route path="/shortInsurances" element={<ShortInsurancesCarPage />} />
          </Route>

          <Route path="/shortInsurance" element={<WizardLayout />}>
            <Route path="/shortInsurance" element={<ShortInsurancePage />} />
            <Route path="/shortInsurance/:insuranceParamsId" element={<ShortInsurancePage />} />
          </Route>
        </Route>

        <Route element={<AppLayout app={null} />}>
          <Route element={<BasicLayout />}>
            <Route path="/profile">
              <Route index element={<ProfilePage />} />
            </Route>

            <Route path="/user">
              <Route path="/user/list" element={<UserListPage />} />
              <Route path="/user/:userId" element={<UserDetailPage />} />
              <Route path="/user/:userId/edit" element={<UserEditPage />} />
            </Route>

            <Route path="/organization">
              <Route path="/organization" element={<OrganizationCurrentPage />} />
              <Route path="/organization/list" element={<OrganizationListPage />} />
              <Route path="/organization/create" element={<OrganizationCreatePage />} />
              <Route path="/organization/:organizationUnitId" element={<OrganizationDetailPage />} />
              <Route path="/organization/:organizationUnitId/edit" element={<OrganizationEditPage />} />
              <Route path="/organization/:organizationUnitId/createDependent" element={<OrganizationCreateDependentPage />} />
              <Route path="/organization/:organizationUnitId/createUser" element={<OrganizationCreateUserPage />} />
            </Route>

            <Route path='/ppzAuthorization'>
              <Route path="/ppzAuthorization/list" element={<PpzAuthorizationListPage />} />
              <Route path="/ppzAuthorization/create" element={<PpzAuthorizationCreatePage />} />
              <Route path="/ppzAuthorization/:ppzId" element={<PpzAuthorizationDetailPage />} />
            </Route>

            <Route path="/flex-group">
              <Route path="/flex-group/list" element={<FlexGroupListPage />} />
              <Route path="/flex-group/create" element={<FlexGroupCreatePage />} />
              <Route path="/flex-group/:flexGroupId" element={<FlexGroupDetailPage />} />
              <Route path="/flex-group/:flexGroupId/edit" element={<FlexGroupEditPage />} />
            </Route>

            <Route path="/news">
              <Route index element={<NewsPage />} />
              <Route path="/news/admin" element={<NewsAdminPage />} />
              <Route path="/news/admin/create" element={<NewsCreatePage />} />
              <Route path="/news/admin/:appInfoId/edit" element={<AppInfoEditPage />} />
            </Route>

            <Route path="/supervizor">
              <Route path="/supervizor/discounts" element={<DiscountsOverviewPage />} />
              <Route path="/supervizor/setCommissionPaidDates" element={<SetCommissionPaidDates />} />
              <Route path="/supervizor/exports" element={<ExportsPage />} />
              <Route path="/supervizor/static-files" element={<StaticFilesPage />} />
              <Route path="/supervizor/dummy-persons" element={<DummyPersonsPage />} />
              <Route path="/supervizor/dummy-persons/create" element={<DummyPersonEditPage />} />
              <Route path="/supervizor/dummy-persons/edit/:id" element={<DummyPersonEditPage />} />
            </Route>

            <Route path="/apilog">
              <Route path="/apilog/:apiLogId" element={<LogDetailPage />} />
            </Route>

            <Route path="/unauthorized">
              <Route index element={<UnauthorizedPage />} />
            </Route>
          </Route>

          <Route
            path="/notfound"
            element={<ErrorPage error="Obchodní případ nebyl nalezen" />}
          />

        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
      <Route path="/app-offline" element={<AppOffline />} />
    </Route>,
  )

  const router = sentryCreateBrowserRouter(routes, {
    basename: getBaseUrl(),
  })

  return <RouterProvider router={router} />
}
